import React, { useState, useEffect } from 'react';
import Tabs, { TabPane } from 'rc-tabs';
import Overview from './components/overview';
import EmailAuthenticationTable from './components/email-authentication-table';
import {
  EmailReputationStatus,
  SpamAssassinScoreReportFilter,
  TestType,
} from '../../../../../../enums/inbox-radar';
import {
  TestConfigResponse,
  TestReportSummaryResponse,
  SpamAssassinScore,
} from '../../../../../../types';
import SenderEmailAccounts from '../sender-email-accounts';
import SpamAssassinReportModal from '../sender-email-accounts/components/spam-assassin-report-modal';
import { Images } from '../../../../../../../../shared/app-constants';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';

const AccountResults: React.FC<{
  error?: any;

  testConfigData?: TestConfigResponse;
  testReportListData?: any;
  testReportSummaryData?: TestReportSummaryResponse;
  scrollContainerRef?: React.RefObject<HTMLDivElement>;
  testReportSenderEmailAccountList: any;
  onLoadMore: () => void;
  isLoadingMore: boolean;
  forShareableReport?: boolean;
  testId?: string;
  sendGetSpamAssassinReportRequest?: (
    seedTestId: string,
    payload: { seedTestSenderEmailAccountId: number },
    filter?: SpamAssassinScoreReportFilter,
  ) => void;
  spamAssassinReportResponse?: any;
  isLoadingSpamAssassinReport?: boolean;
  sendGetEmailAuthenticationRequest?: (
    seedTestId: string,
    payload: {
      senderEmailAccountIds?: number[];
      limit: number;
      page: number;
      reputationStatus?: EmailReputationStatus;
    },
  ) => void;
  emailAuthenticationReportResponse?: any;
  firebaseData?: any;
  emailReputationRequestStatus?: RequestStatus;
}> = ({
  error,
  testConfigData,
  testReportListData,
  testReportSummaryData,
  scrollContainerRef,
  testReportSenderEmailAccountList,
  onLoadMore,
  isLoadingMore,
  forShareableReport = false,
  testId,
  sendGetSpamAssassinReportRequest,
  spamAssassinReportResponse,
  isLoadingSpamAssassinReport,
  sendGetEmailAuthenticationRequest,
  emailAuthenticationReportResponse,
  firebaseData,
  emailReputationRequestStatus,
}) => {
  const [activeTab, setActiveTab] = useState('overview');
  const [
    showSpamAssassinReportModal,
    setShowSpamAssassinReportModal,
  ] = useState(false);
  const [
    spamAssassinReportModalData,
    setSpamAssassinReportModalData,
  ] = useState<SpamAssassinScore>(null);
  const [currentFromEmail, setCurrentFromEmail] = useState<string>('');

  const handleTabChange = (activeKey: string) => {
    setActiveTab(activeKey);
  };

  const handleSpamScoreClick = (row: any) => {
    if (testId && row?.seedTestSenderEmailAccountId) {
      // Call the spam report API
      sendGetSpamAssassinReportRequest(testId, {
        seedTestSenderEmailAccountId: row.seedTestSenderEmailAccountId,
      });
      setCurrentFromEmail(row?.fromEmail || '');
      setShowSpamAssassinReportModal(true);
    }
  };

  const isWaitingForEmail =
    testReportSummaryData?.inboxPlacement?.sentCount !== null &&
    testReportSummaryData?.inboxPlacement?.sentCount === 0;

  const didntReceiveEmail =
    testReportSummaryData?.inboxPlacement?.sentCount !== null &&
    testReportSummaryData?.inboxPlacement?.sentCount > 0 &&
    testReportSummaryData?.inboxPlacement?.sentCount ===
      testReportSummaryData?.inboxPlacement?.notDelivered?.value;

  // Show modal when API response is received
  useEffect(() => {
    if (
      spamAssassinReportResponse?.spamAssassinScore &&
      !isLoadingSpamAssassinReport &&
      spamAssassinReportResponse.spamAssassinScore.spamReport?.tests?.length > 0
    ) {
      setSpamAssassinReportModalData(
        spamAssassinReportResponse.spamAssassinScore,
      );
    }
  }, [spamAssassinReportResponse, isLoadingSpamAssassinReport]);

  return (
    <div className="account-results-container">
      <span className="account-results-header">Placement Insights</span>
      <div className="account-results-tabs">
        {testReportSenderEmailAccountList?.length === 0 &&
        testConfigData?.type === TestType.MANUAL_EXTERNAL ? (
          <div className="manual-external-no-data-container">
            <div className="manual-external-no-data-content">
              <div className="no-data-icon">
                <img
                  src={
                    isWaitingForEmail
                      ? Images.WaitingForEmail
                      : Images.EmailNotReceived
                  }
                  alt="no email data"
                />
              </div>
              <div className="no-data-heading">
                <span>
                  {isWaitingForEmail
                    ? 'Waiting for your Email'
                    : 'We Didn’t Receive Your Email'}
                </span>
              </div>
              <div className="no-data-description">
                {isWaitingForEmail && (
                  <span>
                    If you’ve sent it already, it might still be on the way.{' '}
                    <br />
                    Otherwise, send it now - the test ends soon.
                  </span>
                )}
                {didntReceiveEmail && (
                  <span>
                    Your test has completed, but we couldn’t detect any incoming
                    emails.
                    <br />
                    Check your email setup and re-run the test.
                  </span>
                )}
              </div>
            </div>
          </div>
        ) : (
          <Tabs
            prefixCls="bs-tabs"
            className="bs-tabs-small"
            activeKey={activeTab}
            onChange={handleTabChange}
          >
            <TabPane tab="Overview" key="overview">
              <Overview
                scrollContainerRef={scrollContainerRef}
                testReportOverviewData={testReportListData?.seedTests}
                onSpamScoreClick={handleSpamScoreClick}
                hasMoreSenderEmailAccounts={testReportListData?.hasMore}
                onLoadMore={onLoadMore}
                isLoadingMore={isLoadingMore}
                firebaseData={firebaseData?.senderProgress}
                sentCount={testReportSummaryData?.inboxPlacement?.sentCount}
              />
            </TabPane>
            <TabPane tab="Authentication" key="authentication">
              <EmailAuthenticationTable
                scrollContainerRef={scrollContainerRef}
                emailReputationData={testReportListData?.seedTests}
                hasMoreEmailReputation={testReportListData?.hasMore}
                isLoading={isLoadingMore}
                error={error}
                onLoadMore={onLoadMore}
                isLoadingMore={isLoadingMore}
                sendGetEmailAuthenticationRequest={
                  sendGetEmailAuthenticationRequest
                }
                testId={testId}
                emailAuthenticationReportResponse={
                  emailAuthenticationReportResponse
                }
                firebaseData={firebaseData?.senderProgress}
                emailReputationRequestStatus={emailReputationRequestStatus}
              />
            </TabPane>
            <TabPane tab="Detailed Analysis" key="analysis">
              <SenderEmailAccounts
                scrollContainerRef={scrollContainerRef}
                testReportSenderEmailAccountList={testReportListData?.seedTests}
                hasMoreSenderEmailAccounts={testReportListData?.hasMore}
                onLoadMore={onLoadMore}
                isLoadingMore={isLoadingMore}
                forShareableReport={forShareableReport}
                configType={testConfigData?.type}
                inboxPlacement={testReportSummaryData?.inboxPlacement}
                testId={testId}
                sendGetSpamAssassinReportRequest={
                  sendGetSpamAssassinReportRequest
                }
                spamAssassinReportResponse={spamAssassinReportResponse}
                isLoadingSpamAssassinReport={isLoadingSpamAssassinReport}
                testConfigData={testConfigData}
                firebaseData={firebaseData?.senderProgress}
              />
            </TabPane>
          </Tabs>
        )}
      </div>
      <SpamAssassinReportModal
        show={showSpamAssassinReportModal}
        onClose={() => {
          setShowSpamAssassinReportModal(false);
          setCurrentFromEmail('');
        }}
        data={spamAssassinReportModalData}
        selectedEmailAccountId={null}
        keySummaryModal={true}
        isLoading={isLoadingSpamAssassinReport}
        isFromEmail={currentFromEmail}
      />
    </div>
  );
};

export default AccountResults;
